import { useRouter } from 'next/navigation';
import { useTree } from './useTree';
import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useDialogStore } from '@/stores/dialogStore';
import { log } from '@/lib/utils/logger';
import { TreeNode } from '@/types/tree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useTreeStore } from '@/stores/treeStore';
import { TreeNodeFactory } from '@/lib/tree/treeNodeFactory';
import { ActivityCategoryDto } from '@/api/ApiClient';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { handleDeleteError } from '@/lib/tree/deleteEntityUtils';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { extractEntityIdFromDataId, extractNumericEntityIdFromDataId } from '@/lib/tree/treeUtils';

/**
 * Custom hook for deleting entities with consistent logic across the app
 */
export const useDeleteEntity = () => {
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { showDeleteDialog, showConfirmDialog } = useDialogStore();
  const { deleteNode, reloadNode, getSampleActivityData, getSampleData } = useTree();
  const { selectNode } = useTreeSelection();
  const setLoading = useTreeStore((state) => state.setLoading);
  const getData = useTreeStore((state) => state.getData);
  // Track delete-in-progress state per *dataId* so it lines up with useDataActions().
  const markDeleting = (dataId: string, flag: boolean) => setLoading(`delete-${dataId}`, flag);
  const router = useRouter();
  const systemConfig = useSystemConfig();
  /**
   * Performs the actual delete operation based on node type
   * Uses shared utility for backend calls, then handles UI-specific logic
   */
  const performDelete = async (node: TreeNode, confirmed = false): Promise<void> => {
    // Perform backend deletion first
    switch (node.type) {
      case 'sample': {
        await backend.sample.deletePending(extractEntityIdFromDataId(node.dataId).toString(), confirmed);
        break;
      }
      case 'sampleActivity': {
        await backend.sampleActivity.deletePending(extractNumericEntityIdFromDataId(node.dataId), confirmed);
        break;
      }
      case 'project': {
        const project = getData(node.dataId);
        if (!project) {
          throw new Error(`Project not found for data ID: ${node.dataId}`);
        } else {
          await backend.project.delete(project.id);
        }
        break;
      }
      case 'publication': {
        await backend.publication.delete(extractNumericEntityIdFromDataId(node.dataId).toString());
        break;
      }
      case 'projectActivity': {
        await backend.projectActivity.deletePending(extractNumericEntityIdFromDataId(node.dataId), confirmed);
        break;
      }
      case 'theoryActivity': {
        await backend.theoryActivity.deletePending(extractNumericEntityIdFromDataId(node.dataId), confirmed);
        break;
      }
      case 'communication': {
        await backend.communication.delete(extractNumericEntityIdFromDataId(node.dataId));
        break;
      }
      case 'dataPackage': {
        await backend.data.deletePending(extractNumericEntityIdFromDataId(node.dataId).toString());
        break;
      }
      case 'shipping': {
        await backend.shipping.delete(extractNumericEntityIdFromDataId(node.dataId));
        break;
      }
      default:
        throw new Error(`Unsupported node type: ${node.type}`);
    }

    // Only update UI after successful backend deletion
    // Remove from tree store
    deleteNode(node.nodeId);

    // Handle navigation after deletion
    if (node.parentId) {
      // For shipping, select the project (grandparent) rather than the shippings container
      let nodeToSelect = node.parentId;
      if (node.type === 'shipping') {
        const nodes = useTreeStore.getState().nodes;
        const parentNode = nodes.get(node.parentId);
        if (parentNode?.parentId) {
          nodeToSelect = parentNode.parentId;
        }
      }
      selectNode(nodeToSelect);

      // If this was a storage activity, reload the parent sample to get updated sample location
      if (node.type === 'sampleActivity') {
        const activityData = getSampleActivityData(node);
        if (activityData?.category === ActivityCategoryDto.Storage) {
          try {
            await reloadNode(node.parentId, backend);
            log.debug('[useDeleteEntity] Reloaded parent sample after storage activity deletion');
          } catch (reloadError) {
            log.error('[useDeleteEntity] Failed to reload parent sample after storage activity deletion:', reloadError);
            // Don't show error to user as the main delete was successful
          }
        }
      }
    } else {
      const basePath = systemConfig?.basePath ?? '';
      const listUrl =
        node.type === 'sample' ? `${basePath}/samples` : node.type === 'project' ? `${basePath}/projects` : null;

      if (listUrl) {
        router.push(listUrl);
      }
      // Other node types should never be the root node.
    }

    showSnackbar(`${TreeNodeFactory.getNodeTypeLabel(node, getData)} ${node.label} deleted successfully`, 'success');
  };

  /**
   * Deletes an entity with confirmation dialog and error handling
   * This is the main function to call from components
   */
  const deleteEntity = async (node: TreeNode): Promise<boolean> => {
    try {
      // Special handling for Inventory activities: they cannot be deleted independently.
      // Deleting an inventory activity requires deleting the parent sample as well.
      if (node.type === 'sampleActivity') {
        const activityData = getSampleActivityData(node);
        if (activityData?.category === ActivityCategoryDto.Inventory) {
          // Get parent sample node
          const nodes = useTreeStore.getState().nodes;
          const parentSampleNode = node.parentId ? nodes.get(node.parentId) : null;

          if (!parentSampleNode) {
            showSnackbar('Parent sample not found. Cannot delete inventory activity.', 'error');
            return false;
          }

          // Get sample data for display in confirmation dialog
          const sampleData = getSampleData(parentSampleNode);
          const sampleLabel = sampleData?.sampleLabel || 'Unknown';

          // Show custom confirmation dialog explaining that sample will also be deleted
          const confirmed = await showConfirmDialog({
            title: 'Delete Inventory Activity',
            message: `Deleting this inventory activity will also delete the sample "${sampleLabel}". This action cannot be undone. Are you sure you want to proceed?`,
            confirmText: 'Delete Activity and Sample',
            isDestructive: true,
          });

          if (!confirmed) {
            return false;
          }

          // Delete the parent sample instead (which will cascade delete the inventory activity).
          // Mark the *sample data* as being deleted; the inventory activity will be removed as part of that.
          markDeleting(parentSampleNode.dataId, true);
          try {
            await performDelete(parentSampleNode, false);
            return true;
          } catch (err) {
            // We are about to show a confirmation dialog (428) or an error message for the sample.
            // Clear the deleting flag so the blocking backdrop does not cover the dialog.
            markDeleting(parentSampleNode.dataId, false);

            // Handle errors from sample deletion
            const errorResult = await handleDeleteError(err, parentSampleNode, {
              showSnackbar,
              showConfirmDialog,
              getData,
            });

            if (errorResult.shouldRetry) {
              // User confirmed force-delete on the sample: show the deleting backdrop again while retrying.
              markDeleting(parentSampleNode.dataId, true);
              await performDelete(parentSampleNode, true);
              return true;
            }

            return false;
          } finally {
            markDeleting(parentSampleNode.dataId, false);
          }
        }
      }

      // Standard deletion flow for all other entity types.
      // Publications use KO-parity wording because backend delete hides, not hard-deletes.
      const confirmed =
        node.type === 'publication'
          ? await showConfirmDialog({
              title: 'Remove publication',
              message: 'Do you want to remove this from the publication list? It will be hidden, but not deleted.',
              confirmText: 'Yes',
              cancelText: 'No',
              isDestructive: true,
            })
          : await showDeleteDialog(TreeNodeFactory.getNodeTypeLabel(node), node.label);
      if (!confirmed) {
        return false;
      }

      // Mark this node's data as being deleted while the backend call is in-flight.
      markDeleting(node.dataId, true);

      try {
        await performDelete(node, false); // First attempt without force
        return true;
      } catch (err) {
        // We are about to show a confirmation dialog (428) or an error message.
        // Turn off the deleting flag so the blocking backdrop does not cover the dialog.
        markDeleting(node.dataId, false);

        // Handle specific error cases that might require confirmation using shared utility
        const errorResult = await handleDeleteError(err, node, {
          showSnackbar,
          showConfirmDialog,
          getData,
        });

        if (errorResult.shouldRetry) {
          // User confirmed force-delete: show the deleting backdrop again while retrying.
          markDeleting(node.dataId, true);
          await performDelete(node, true);
          return true;
        }

        // If shouldAbort is true, error handling already showed appropriate message
        return false;
      }
    } catch (error) {
      log.error('[useDeleteEntity] Unexpected error in deleteEntity:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to delete entity.', 'error');
      return false;
    } finally {
      // Ensure deleting flag is cleared in all cases so the backdrop is removed.
      markDeleting(node.dataId, false);
    }
  };

  return {
    deleteEntity,
  };
};
